import { trackError } from '@iheartjane/tracking';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { PropsWithChildren } from 'react';

import type { ExternalCart, HeadlessOptions } from '@jane/shared-ecomm/types';

import type { HeadlessAppContextType } from './headlessAppProvider.types';

type maybeString = string | undefined;

const HeadlessAppContext = createContext<HeadlessAppContextType>({
  externalId: undefined,
  headlessCheckoutPartnerId: undefined,
  headlessPartnerName: undefined,
  headlessStoreId: undefined,
  loadHeadlessApp: () => null,
  options: {},
});

export const HeadlessAppProvider = ({ children }: PropsWithChildren) => {
  const [externalId, setExternalId] = useState<maybeString>(undefined);
  const [headlessPartnerName, setHeadlessPartnerName] =
    useState<maybeString>(undefined);
  const [headlessCheckoutPartnerId, setHeadlessCheckoutPartnerId] =
    useState<maybeString>(undefined);
  const [headlessStoreId, setHeadlessStoreId] =
    useState<maybeString>(undefined);
  const [options, setOptions] = useState<HeadlessOptions | undefined>({});

  const loadHeadlessApp = useCallback((externalCart: ExternalCart) => {
    const {
      headlessCheckoutPartnerId,
      headlessPartnerName,
      options,
      storeId,
      user,
    } = externalCart;

    if (options?.disableGuestCheckout && !options?.loginUrl) {
      trackError('HeadlessApp has guest checkout disabled and no loginUrl', {
        storeId: externalCart.storeId,
      });
    }

    if (headlessCheckoutPartnerId) {
      setHeadlessCheckoutPartnerId(headlessCheckoutPartnerId);
    }

    if (headlessPartnerName) {
      setHeadlessPartnerName(headlessPartnerName);
    }

    if (storeId) {
      setHeadlessStoreId(storeId);
    }

    if (user) {
      setExternalId(user.externalId);
    }

    if (options) {
      setOptions((prevValue) => ({ ...prevValue, ...options }));
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      externalId,
      headlessPartnerName,
      headlessCheckoutPartnerId,
      headlessStoreId,
      loadHeadlessApp,
      options,
    }),
    [
      externalId,
      headlessPartnerName,
      headlessCheckoutPartnerId,
      headlessStoreId,
      loadHeadlessApp,
      options,
    ]
  );
  return (
    <HeadlessAppContext.Provider value={contextValue}>
      {children}
    </HeadlessAppContext.Provider>
  );
};

// Instead of using a "safe" hook, or checking for actual context values,
// we create this context with the default values above so it always returns something.
export const useHeadlessApp = () => useContext(HeadlessAppContext);
